import React, { isValidElement, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  DialogContentText,
  DialogContent,
  withStyles,
} from "@material-ui/core";
import useWindowResizeListener from "hooks/useWindowResizeListener";
import style from "style";

/**
 * This is a Context Provider that allows
 * the developer to be able to utilize and access a dialog component globally.
 * Just by firing the @function openDialog, the dialog Component will show up
 * and to close or hide the component, the @function closeDialog needs to be fired.
 */

export const FormDialogContext = React.createContext({
  componentToRender: <></>,
  open: false,
  openDialog: () => {},
  closeDialog: () => {},
  triggerClose: () => {},
});

const FormDialogProvider = ({ children, classes }) => {
  const [componentToRender, setComponentToRender] = useState(<></>);
  const [title, setTitle] = useState("");
  const [open, setIsOpen] = useState(false);
  const [hideDefaultButton, setHideDefaultButton] = useState(false);
  const [attemptToClose, setAttemptToClose] = useState(false);
  const [shouldStrecth, setShouldStretch] = useState(false);
  const [dontShowCancelDialog, setDontShowCancelDialog] = useState(false);
  const [shouldGoFullScreen, setShouldGoFullScreen] = useState(true);

  const { windowWidth } = useWindowResizeListener();

  const fullScreen = windowWidth <= 936;

  /**
   * The Dialog will only showup if the
   * @param {ReactComponent} component
   * is confirmed to be a valid React Element.
   * Otherwise, it will throw an Error.
   */
  const openDialog = (
    component,
    titlePayload,
    shouldHideDefaultButtons = false,
    stretch = false,
    dontShowCancelDialog = false,
    goFullScreen = true
  ) => {
    if (component === null) {
      throw Error("Component To Be Displayed Cannot Be Null");
    }

    if (isValidElement(component)) {
      setComponentToRender(component);
      setIsOpen(true);
      setTitle(titlePayload);
      setHideDefaultButton(shouldHideDefaultButtons);
      setShouldStretch(stretch);
      setDontShowCancelDialog(dontShowCancelDialog);
      setShouldGoFullScreen(goFullScreen);

      return;
    }

    throw Error("Component Must Be A Valid Element");
  };

  const closeDialog = () => {
    revokeClose();

    setIsOpen(false);
  };

  const triggerClose = () => {
    if (dontShowCancelDialog) {
      closeDialog();
      return;
    }
    setAttemptToClose(true);
  };

  const revokeClose = () => {
    setAttemptToClose(false);
  };

  const HeaderTitle = () => (title ? <DialogTitle>{title}</DialogTitle> : null);

  return (
    <FormDialogContext.Provider
      value={{
        componentToRender,
        open,
        openDialog,
        closeDialog,
        triggerClose,
      }}
    >
      <Dialog
        fullScreen={fullScreen && shouldGoFullScreen}
        aria-describedby="alert-dialog-description"
        aria-labelledby="form-dialog-title"
        open={open}
        classes={{
          paperScrollPaper: shouldStrecth ? classes.customMaxWidth : null,
        }}
      >
        <HeaderTitle />
        {componentToRender}
        {!hideDefaultButton && (
          <DialogActions>
            <Button onClick={triggerClose} color="secondary">
              Cancel
            </Button>
          </DialogActions>
        )}
      </Dialog>
      {children}
      {/* // ------------------ confirmation message */}
      <Dialog
        open={attemptToClose}
        onClose={revokeClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to close?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={revokeClose} color="primary" autoFocus>
            No
          </Button>
          <Button onClick={closeDialog} color="primary" autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </FormDialogContext.Provider>
  );
};

export default withStyles(style)(FormDialogProvider);
